import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Header from './components/Header'
import WhatsAppFAB from './components/WhatsAppFAB'
import SearchPage from './pages/SearchPage'
import AuthPage from './pages/AuthPage'
import ProfilePage from './pages/ProfilePage'
import AdminPage from './pages/AdminPage'
import ResetPinPage from './pages/ResetPinPage'
import ContactModal from './components/ContactModal'
import { useAuthStore } from './store/authStore'
import { buildAuthWhatsAppURL } from './config/whatsapp'
import { FaWhatsapp } from 'react-icons/fa'
import { FiMail } from 'react-icons/fi'
import { initializeFirebase } from './config/firebase'

// Shared contact buttons for declined / pending screens
function ContactButtons({ onContactClick }) {
  return (
    <div className="flex gap-3 justify-center pt-2">
      <a
        href={buildAuthWhatsAppURL()}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-4 py-2 rounded bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm hover:bg-[#25D366]/20 transition"
      >
        <FaWhatsapp size={16} /> WhatsApp Us
      </a>
      <button
        type="button"
        onClick={onContactClick}
        className="flex items-center gap-2 px-4 py-2 rounded bg-gray-700/30 border border-border-dark text-gray-300 text-sm hover:bg-gray-700/50 transition"
      >
        <FiMail size={15} /> Contact Us
      </button>
    </div>
  )
}

function App() {
  const { isAuthenticated, isApproved, isDeclined, isAdmin, user, initAuth, logout } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [showContactModal, setShowContactModal] = useState(false)

  useEffect(() => {
    initializeFirebase()
    initAuth().then(() => setLoading(false))
  }, [initAuth])

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-yellow"></div>
      </div>
    )
  }

  // Signed in but declined
  if (isAuthenticated && isDeclined) {
    return (
      <>
        {showContactModal && <ContactModal onClose={() => setShowContactModal(false)} />}
        <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center space-y-6">
            <h1 className="text-3xl font-bold text-accent-yellow">Case Sizing Engine</h1>
            <div className="card space-y-4">
              <div className="text-5xl">✗</div>
              <h2 className="text-xl font-bold text-white">Access Not Approved</h2>
              <p className="text-gray-400 text-sm">
                Unfortunately your access request for <span className="text-white font-semibold">{user?.companyName || user?.email}</span> was not successful at this time.
              </p>
              <p className="text-gray-400 text-sm">Please contact us via WhatsApp or email for assistance.</p>
              <ContactButtons onContactClick={() => setShowContactModal(true)} />
              <button type="button" onClick={logout} className="text-xs text-gray-500 hover:text-gray-300 transition mt-2">
                Sign out
              </button>
            </div>
            <p className="text-gray-500 text-xs">© 2026 - Case Sizing Engine</p>
          </div>
        </div>
      </>
    )
  }

  // Signed in but not yet approved
  if (isAuthenticated && !isApproved) {
    return (
      <>
        {showContactModal && <ContactModal onClose={() => setShowContactModal(false)} />}
        <div className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center space-y-6">
            <h1 className="text-3xl font-bold text-accent-yellow">Case Sizing Engine</h1>
            <div className="card space-y-4">
              <div className="text-5xl">⏳</div>
              <h2 className="text-xl font-bold text-white">Awaiting Approval</h2>
              <p className="text-gray-400 text-sm">
                Your account for <span className="text-white font-semibold">{user?.companyName || user?.email}</span> has been registered and is pending approval.
                Approval may take a few hours. If urgent, please contact us directly.
              </p>
              <p className="text-gray-500 text-xs">In the meantime, feel free to contact us directly.</p>
              <ContactButtons onContactClick={() => setShowContactModal(true)} />
              <button type="button" onClick={logout} className="text-xs text-gray-500 hover:text-gray-300 transition mt-2">
                Sign out
              </button>
            </div>
            <p className="text-gray-500 text-xs">© 2026 - Case Sizing Engine</p>
          </div>
        </div>
      </>
    )
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-bg-dark text-gray-200">
        <Header />
        {isAuthenticated && isApproved && <WhatsAppFAB />}
        <Routes>
          <Route path="/reset-pin" element={<ResetPinPage />} />
          {isAuthenticated && isApproved ? (
            <>
              <Route path="/" element={<SearchPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              {isAdmin && <Route path="/admin" element={<AdminPage />} />}
              <Route path="/auth" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/" element={<Navigate to="/auth" replace />} />
              <Route path="*" element={<Navigate to="/auth" replace />} />
            </>
          )}
        </Routes>
      </div>
    </BrowserRouter>
  )
}

export default App
